import { RefObject, useCallback, useEffect, useState } from 'react';
import { COL_GAP, MAX_COL_WIDTH, MIN_COL_WIDTH } from '../constants';

export function useSizes(containerRef: RefObject<HTMLDivElement | null>) {
  const [containerWidth, setContainerWidth] = useState(0);

  const handleResize = useCallback(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  }, [containerRef]);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(() => handleResize());
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [containerRef, handleResize]);

  let numberOfColumns = 1;
  let itemWidth = MIN_COL_WIDTH;

  if (containerWidth > 0) {
    const fixedPadding = 2 * COL_GAP;
    const maxColumnsByMinWidth = Math.floor((containerWidth - fixedPadding) / (MIN_COL_WIDTH + COL_GAP));

    // Check for min width
    numberOfColumns = Math.max(1, Math.min(5, maxColumnsByMinWidth));
    itemWidth = Math.floor((containerWidth - fixedPadding - (numberOfColumns - 1) * COL_GAP) / numberOfColumns);

    // Check for max width
    const numberOfColumnsWithMaxItemWidth = Math.max(
      1,
      Math.min(5, Math.floor((containerWidth - fixedPadding) / (MAX_COL_WIDTH + COL_GAP))),
    );

    if (itemWidth > MAX_COL_WIDTH && numberOfColumnsWithMaxItemWidth >= numberOfColumns) {
      itemWidth = MAX_COL_WIDTH;
      numberOfColumns = numberOfColumnsWithMaxItemWidth;
    }

    itemWidth = Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, itemWidth));
  }

  /**
   * Until the ResizeObserver has reported a width, the values above are the
   * single-column fallback rather than the real layout. Callers that resolve a
   * row index (scroll-to-page) must wait for this, or they resolve against a
   * layout that is about to change under them.
   */
  return { numberOfColumns, /* containerWidth, */ itemWidth, hasMeasuredWidth: containerWidth > 0 };
}
